import { db } from '@/api/firebase';
import { useToast } from '@/components/ui/use-toast';
import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  updateDoc,
} from 'firebase/firestore';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import BreadcrumbComponent from '@/components/breadcrumb';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useMainContext } from '@/context/main-context';

const weekDays = [
  { value: 'monday', label: 'Dushanba' },
  { value: 'tuesday', label: 'Seshanba' },
  { value: 'wednesday', label: 'Chorshanba' },
  { value: 'thursday', label: 'Payshanba' },
  { value: 'friday', label: 'Juma' },
  { value: 'saturday', label: 'Shanba' },
  { value: 'sunday', label: 'Yakshanba' },
];

function AddGroup() {
  const { uid, courses, teachers, activeBranchId, rooms } = useMainContext();

  const [startDate, setStartDate] = useState('');
  const [selectedRoom, setSelectedRoom] = useState('');
  const [selectedDay, setSelectedDay] = useState('');
  const [customDays, setCustomDays] = useState([]);

  const defaultValue = {
    courseId: '',
    teacherId: '',
    groupNumber: '',
    dayType: '',
    from: '',
    to: '',
  };

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({ defaultValues: defaultValue });

  const { toast } = useToast();

  const onSubmit = async (data) => {
    try {
      const userGroupsRef = collection(db, `users/${uid}/groups`);

      const weekDays =
        selectedDay === 'custom' ? customDays : dayTypes[selectedDay] || [];

      const payload = {
        ...data,
        branchId: activeBranchId,
        status: true,
        startDate: new Date(startDate).getTime(),
        timestamp: new Date().getTime(),
        roomId: selectedRoom || null,
        weekDays,
        // ✅ Eski format bilan moslik uchun
        timeInDay: `${data.from} - ${data.to}`,
      };

      // Guruhni qo‘shish
      const newDoc = await addDoc(userGroupsRef, payload);

      // Xonaga biriktirish (agar tanlangan bo‘lsa)
      if (selectedRoom) {
        const roomRef = doc(db, `users/${uid}/rooms/${selectedRoom}`);
        await updateDoc(roomRef, {
          groups: arrayUnion({
            groupId: newDoc.id,
            days: weekDays,
            from: data.from,
            to: data.to,
            // optional: eski format ham qo‘shib yuborsangiz bo‘ladi
            timeInDay: `${data.from} - ${data.to}`,
          }),
        });
      }

      // Reset
      reset();
      setStartDate('');
      setSelectedRoom('');
      setSelectedDay('');
      setCustomDays([]);
      toast({
        title: "Guruh muvaffaqiyat qo'shildi",
      });
    } catch (error) {
      console.log(error);
      toast({
        title: 'Xatolik yuz berdi',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const dayTypes = {
    every_day: [
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
      'sunday',
    ],
    odd: ['monday', 'wednesday', 'friday'],
    even: ['tuesday', 'thursday', 'saturday'],
  };
  const getSelectedDays = () => {
    if (selectedDay === 'custom') return customDays;
    return dayTypes[selectedDay] || [];
  };

  const getAvailableTimes = () => {
    const allTimes = Array.from(
      { length: 23 },
      (_, i) => `${(i + 1).toString().padStart(2, '0')}:00`
    );

    // Xona tanlanmagan bo‘lsa → hammasi available
    if (!selectedRoom) {
      return allTimes.map((t) => ({
        value: t,
        disabled: false,
      }));
    }

    const room = rooms.find((r) => r.id === selectedRoom);
    if (!room) return [];

    const selectedDays = getSelectedDays();

    // Shu xonadagi band vaqtlarni olish
    const busyTimes =
      room.groups
        ?.filter((g) => g.days.some((d) => selectedDays.includes(d)))
        .map((g) => [g.from, g.to]) || [];

    // Band bo'lgan vaqtlarni disable qilish
    const isBusy = (time) => {
      return busyTimes.some(([from, to]) => time >= from && time < to);
    };

    return allTimes.map((t) => ({
      value: t,
      disabled: isBusy(t),
    }));
  };

  return (
    <div className="px-4 lg:px-8 mx-auto space-y-4 my-4">
      <BreadcrumbComponent title="O'quvchi qo'shish" />

      <div>
        <h2 className="text-2xl font-bold tracking-tight">O'quvchi qo'shish</h2>
        <p className="text-muted-foreground">
          Tizimga yangi oʻquvchi qoʻshish uchun quyidagi shaklni toʻldiring.
        </p>
      </div>

      <div className="xl:flex">
        <div className="xl:w-2/3">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-4">
            {/* Course & Teacher */}
            <div className="flex flex-col md:flex-row gap-2">
              <div className="w-full">
                <Label>Select Course</Label>
                <Controller
                  name="courseId"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select Course" />
                      </SelectTrigger>
                      <SelectContent>
                        {courses.map((item) => (
                          <SelectItem key={item.id} value={item.id}>
                            {item.courseTitle}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.courseId && (
                  <span className="text-red-500">Required</span>
                )}
              </div>

              <div className="w-full">
                <Label>Select Teacher</Label>
                <Controller
                  name="teacherId"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select Teacher" />
                      </SelectTrigger>
                      <SelectContent>
                        {teachers.map((item) => (
                          <SelectItem key={item.id} value={item.id}>
                            {item.fullName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.teacherId && (
                  <span className="text-red-500">Required</span>
                )}
              </div>
            </div>

            {/* Group Number & Start Date */}
            <div className="flex flex-col md:flex-row items-center gap-2">
              <div className="w-full">
                <Label>Group Number</Label>
                <Input
                  type="number"
                  placeholder="Group Number"
                  {...register('groupNumber', { required: true })}
                />
                {errors.groupNumber && (
                  <span className="text-red-500">Required</span>
                )}
              </div>
              <div className="flex flex-col w-full mt-2 space-y-1">
                <Label>Start Date</Label>
                <DatePicker
                  setData={setStartDate}
                  data={startDate}
                  className="w-full"
                />
              </div>
            </div>

            <div className="flex flex-col md:flex-row items-center gap-2">
              {/* Room (optional) */}
              <div className="w-full">
                <Label optional>Xona</Label>
                <Select
                  onValueChange={setSelectedRoom}
                  value={selectedRoom}
                  defaultValue="no-room"
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Xonani tanlang" />
                  </SelectTrigger>
                  <SelectContent value="no-room">
                    <SelectItem>Xona tanlanmasin</SelectItem>
                    {rooms.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.name} (Capacity {r.capacity})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Day Type */}
              <div className="w-full">
                <Label>Hafta kunlari</Label>
                <Select onValueChange={setSelectedDay} value={selectedDay}>
                  <SelectTrigger>
                    <SelectValue placeholder="Hafta kunini tanlang" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="every_day">Har kuni</SelectItem>
                    <SelectItem value="odd">Toq kunlar</SelectItem>
                    <SelectItem value="even">Juft kunlar</SelectItem>
                    <SelectItem value="custom">Boshqa kunlar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {selectedDay === 'custom' && (
              <div className="flex flex-wrap gap-2">
                {weekDays.map((day) => (
                  <label key={day.value} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      value={day.value}
                      checked={customDays.includes(day.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setCustomDays((prev) => [...prev, day.value]);
                        } else {
                          setCustomDays((prev) =>
                            prev.filter((d) => d !== day.value)
                          );
                        }
                      }}
                    />
                    {day.label}
                  </label>
                ))}
              </div>
            )}

            {/* Time Range */}
            <div className="flex flex-col md:flex-row gap-2">
              <div className="w-full">
                <Label>Vaqtan</Label>
                <Controller
                  name="from"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="From" />
                      </SelectTrigger>
                      <SelectContent>
                        {getAvailableTimes().map((t) => (
                          <SelectItem
                            key={t.value}
                            value={t.value}
                            disabled={t.disabled}
                          >
                            {t.value}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              <div className="w-full">
                <Label>Vaqtgacha</Label>
                <Controller
                  name="to"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="To" />
                      </SelectTrigger>
                      <SelectContent>
                        {getAvailableTimes().map((t) => (
                          <SelectItem
                            key={t.value}
                            value={t.value}
                            disabled={t.disabled}
                          >
                            {t.value}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>

            <Button type="submit">Add Group</Button>
          </form>
        </div>
        <div className="xl:w-1/2 h-[calc(100vh-160px)] xl:flex hidden items-center">
          <h1 className="text-9xl font-bold uppercase text-border -translate-y-24 -rotate-90">
            ADD Group
          </h1>
        </div>
      </div>
    </div>
  );
}

export default AddGroup;
